import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService, isRefreshSuperseded } from '../auth';
import { AlertService } from '../shared/services/alert.service';
import { FrontendDiagnosticsService, SPARK_DIAGNOSTIC_REQUEST } from './services/frontend-diagnostics.service';
import { Utils } from './utils';

//import { AccountService } from '../services/account.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private alertService: AlertService,
    private frontendDiagnosticsService: FrontendDiagnosticsService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.context.get(SPARK_DIAGNOSTIC_REQUEST)) {
      return next.handle(request);
    }
    return next.handle(request).pipe(catchError(err => {
      if (err.status === 401 || err.status === 403 || request.url.includes('/api/Account/')) {
        this.frontendDiagnosticsService.recordEntry('auth-http-error', {
          Status: err.status,
          Route: request.url.split('?')[0].replace(/^https?:\/\/[^/]+/, '').replace(/\d+/g, ':id'),
          HasAccessToken: this.authService.isSignedIn(),
          AccessExpiresAt: this.authService.tokenInfo?.expires_at,
          RefreshExpiresAt: this.authService.tokenInfo?.refresh_expires_at,
          AccessRemainingMs: this.authService.accessTokenRemainingMs(),
        });
      }
      const isSyncRequest = this.isSparkSyncRequest(request);
      if (isSyncRequest) {
        this.frontendDiagnosticsService.addError('Spark sync request failed: ' + JSON.stringify({
          status: err.status,
          url: request.url,
          method: request.method,
          sessionID: request.body?.PatientSessionID ?? request.body?.ID,
          message: Utils.getAjaxError(err),
          traceId: err.error?.traceId,
        }));
      }
      // Extract error message
      const errorMessage = (err.error && err.error.message) ? err.error.message :
        (err.errors && err.errors.model && err.errors.model.length) ? err.errors.model[0] :
          (err.title) ? err.title :
            (err.error || err.statusText || err.message || 'Unknown error');

      // Check if this is a bad request error for GetPatientInfo with the specific message
      const isGetPatientInfoRequest = request.url.includes('/api/Staff/GetPatientInfo');
      const isBadRequest = err.status === 400;
      if (err.status === 401 || err.status === 403 || isBadRequest) {
        this.frontendDiagnosticsService.addDiagnostic('http-auth-related-error', JSON.stringify({
          status: err.status,
          url: request.url,
          method: request.method,
          message:
            typeof errorMessage === 'string'
              ? errorMessage
              : (errorMessage as any)?.Message || (errorMessage as any)?.message || '',
        }));
      }

      if (isGetPatientInfoRequest && isBadRequest) {
        return throwError(errorMessage.Message);
      }

      // Logout must not renew an expired token and restore a session being cleared.
      if (request.url.includes('/api/Account/Logout')) {
        return throwError(() => err);
      }

      if (err.status === 401 && this.shouldRetryUnauthorizedRequest(request)) {
        return this.authService.refreshTokenOnce().pipe(
          switchMap((token) => {
            if (!token?.access_token) {
              throw err;
            }

            const retryRequest = request.clone({
              setHeaders: {
                Authorization: `Bearer ${token.access_token}`,
                clientname: this.authService.getClientFromUrl(),
              },
            });

            return next.handle(retryRequest);
          }),
          catchError((retryError) => {
            if (isRefreshSuperseded(retryError)) {
              return throwError(() => err);
            }
            // A successful refresh followed by a business/server/network error
            // does not mean the renewed credentials were rejected.
            if (retryError.status !== 401) {
              if (retryError.status === 403 && this.authService.isSignedIn()) {
                this.alertService.error('You do not have permission to access. ' + Utils.getAjaxError(retryError));
              }
              return throwError(() => retryError);
            }
            if (this.isSparkSyncRequest(request)) {
              console.warn(
                'Spark sync request failed after token refresh; keeping local data pending',
                retryError
              );
              return throwError(() => retryError);
            }

            console.error('Unauthorized request ' + request.url + ' signing out');
            this.authService.signOut().subscribe();
            return throwError(errorMessage);
          })
        );
      }

      // Check if this is a permission-related error for supervisor pages
      if ((err.status === 403) && 
          (this.authService.isSignedIn())) {
        // User is signed in but lacks permission for this patient - show error instead of dumping to selector
        console.error('Permission error for supervisor accessing patient', err);
        const permissionErrorMessage = 'You do not have permission to access. ' + errorMessage;
        this.alertService.error(permissionErrorMessage);
      } else if (err.status === 401) {
        if (this.isSparkSyncRequest(request)) {
          console.warn(
            'Spark sync request unauthorized; keeping local data pending'
          );
          return throwError(() => err);
        }

        // Regular unauthorized error handling - redirect to login
        console.error('Unauthorized request ' + request.url + ' signing out');
        this.authService.signOut().subscribe();
      }

      return throwError(() => isSyncRequest ? err : errorMessage);
    }));
  }

  private shouldRetryUnauthorizedRequest(request: HttpRequest<any>): boolean {
    return (
      request.url.includes('/api/') &&
      !request.url.includes('/api/Account/RefreshToken') &&
      this.authService.hasUsableRefreshToken()
    );
  }

  private isSparkSyncRequest(request: HttpRequest<any>): boolean {
    return [
      '/api/Staff/SavePatientSession',
      '/api/Staff/SavePatientTargetData',
      '/api/Staff/SavePatientBehaviorData',
      '/api/Staff/SaveSessionProgressNotes',
      '/api/Staff/UploadGuardianSignature',
      '/api/Staff/UploadStaffHandoffSignature',
    ].some((url) => request.url.includes(url));
  }
}
